import Layout from "../layout";
import { useAuth } from "react-oidc-context";
import { Card } from "react-bootstrap";
import MessageStats from "../../components/messagestats/messagestats";
import TimeFilter from "../../components/TimeFilter/TimeFilter";
import { useMemo, useState, useCallback } from "react";
import React from "react";
import { timeUnit } from "../../app/enums";
import { TopMessagesType } from "../../models/TopMessagesType";
import { ServiceManager } from "../../services/ServiceManager";
import { useQuery } from "@tanstack/react-query";
import HighestMessages from "./HighestMessages";
import PendingMessages from "./PendingMessages";
import EstablishmentsMessages from "./EstablishmentsMessages";
import LogLevelEstablishments from "./LogLevelEstablishments";
import HUBTooltip from "../../components/tooltip";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { jwtDecode } from "jwt-decode";
import { formatDateToLocalISO } from "../../app/common/helper/DateFormatter";
import { ITopMessage } from "../../models/TopMessages";
import { getTopMessageLastSeenDate } from "../../app/common/helper/TopMessageLastSeen";

export default function Dashboard() {
  const auth = useAuth();

  const [selectedTimer, setSelectedTimer] = useState(30);
  const [selectedTimeUnit, setselectedTimeUnit] = useState(timeUnit.minute);
  const [finishDate, setFinishDate] = useState(new Date());
  var defaultStartDate = new Date();
  defaultStartDate.setMinutes(defaultStartDate.getMinutes() - 30);
  const [startDate, setStartDate] = useState(defaultStartDate);

  var jwt: any;

  if (auth.isAuthenticated) {
    jwt = jwtDecode(auth.user?.access_token ?? '', { header: false });
  }

  // Fetch top messages data with caching per date range - only after user selects a filter
  const { isLoading: isLoadingStatic, data } = useQuery({
    queryKey: [`top-messages`, startDate.getTime(), finishDate.getTime()],
    queryFn: () => ServiceManager.MessageService.GetTopMessages(formatDateToLocalISO(startDate), formatDateToLocalISO(finishDate)),
    refetchOnWindowFocus: false
  });

  const filterBySelectedRange = useCallback((messages: ITopMessage[]) => {
    return messages.filter((message) => {
      const lastSeenDate = getTopMessageLastSeenDate(message, finishDate);
      if (!lastSeenDate) {
        return true;
      }

      return lastSeenDate >= startDate && lastSeenDate <= finishDate;
    });
  }, [startDate, finishDate]);

  const groupedTopMessages = useMemo(() => data?.data?.data || [], [data]);

  // Extract data for each message type.
  const highestMessagesDataRaw: ITopMessage[] = useMemo(
    () => groupedTopMessages.find((x: any) => x.type === TopMessagesType.HighestMessages)?.messages || [],
    [groupedTopMessages]
  );
  const pendingMessagesDataRaw: ITopMessage[] = useMemo(
    () => groupedTopMessages.find((x: any) => x.type === TopMessagesType.PendingMessages)?.messages || [],
    [groupedTopMessages]
  );
  const establishmentsMessagesData = useMemo(
    () => groupedTopMessages.find((x: any) => x.type === TopMessagesType.Establishments)?.messages || [],
    [groupedTopMessages]
  );
  const logLevelEstablishmentsData = useMemo(
    () => groupedTopMessages.find((x: any) => x.type === TopMessagesType.LogLevelEstablishments)?.messages || [],
    [groupedTopMessages]
  );

  const highestMessagesData = useMemo(
    () => filterBySelectedRange(highestMessagesDataRaw),
    [filterBySelectedRange, highestMessagesDataRaw]
  );
  const pendingMessagesData = useMemo(
    () => filterBySelectedRange(pendingMessagesDataRaw),
    [filterBySelectedRange, pendingMessagesDataRaw]
  );

  function setTimes(time: any, timeUnit: timeUnit, callBackStartDate: Date, callBackfinishDate: Date, isRefresh?: boolean) {
    if (time === '') {
      return;
    }

    setSelectedTimer(time);
    setselectedTimeUnit(timeUnit);
    setFinishDate(callBackfinishDate);
    setStartDate(callBackStartDate);
  }

  function getFilterDisplayText(): string {
    // For custom date range (timeUnit.year is used for custom ranges)
    if (selectedTimeUnit === timeUnit.year) {
      const start = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const end = finishDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${start} to ${end}`;
    }

    // For predefined time ranges
    const unitLabel: { [key in timeUnit]: string } = {
      [timeUnit.minute]: 'minute',
      [timeUnit.hour]: 'hour',
      [timeUnit.day]: 'day',
      [timeUnit.month]: 'month',
      [timeUnit.year]: 'custom'
    };

    const pluralSuffix = selectedTimer > 1 ? 's' : '';
    return `Last ${selectedTimer} ${unitLabel[selectedTimeUnit]}${pluralSuffix}`;
  }

  return (
    <React.Fragment>
      {auth.isAuthenticated && <Layout title={auth.isAuthenticated && "Welcome " + jwt.name} enableScroll={true}>
        <div className="container-fluid">
          <div className="row">
            <TimeFilter selectedTimer={selectedTimer} selectedTimeUnit={selectedTimeUnit} setTimes={setTimes} isRefresh={true}></TimeFilter>
          </div>
          <div className="row mt-2 mb-3">
            <div className="col-12 d-flex justify-content-end">
              <div className="border rounded-pill px-3 py-2 dashboard-showing-badge">
                Showing: {getFilterDisplayText()}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-4 col-6">
              <MessageStats selectedTimer={selectedTimer} startDate={startDate} finishDate={finishDate} navigateTo={`/message?startDate=${formatDateToLocalISO(startDate)}&finishDate=${formatDateToLocalISO(finishDate)}&timer=${selectedTimer}&unit=${selectedTimeUnit}&fromDashboard=${true}&logLevel=3,4,5`}></MessageStats>
            </div>
          </div>
          <div className="row mt-4">
            <div className="col-6">
              <Card className="stats-table-widget">
                <Card.Header>
                  <Card.Title className="pt-3 ps-2 pb-3 d-flex align-items-center gap-2">Highest Messages
                    <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <HUBTooltip message="Top 10 most frequently occurring log messages. Shows which errors are happening most often." placement="right">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          size="sm"
                        />                    
                      </HUBTooltip>
                    </span>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <HighestMessages data={highestMessagesData} isLoading={isLoadingStatic} />
                </Card.Body>
              </Card>
            </div>
            <div className="col-6">
              <Card className="stats-table-widget">
                <Card.Header>
                  <Card.Title className="pt-3 ps-2 pb-3 d-flex align-items-center gap-2">Pending Messages
                    <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <HUBTooltip message="Top 10 log messages with the longest duration (days between creation and last seen). Shows which errors have been persisting the longest." placement="right">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          size="sm"
                        />
                      </HUBTooltip>
                    </span>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <PendingMessages data={pendingMessagesData} isLoading={isLoadingStatic}/>
                </Card.Body>
              </Card>
            </div>
            <div className="col-6 mt-5">
              <Card className="stats-table-widget h-100">
                <Card.Header>
                  <Card.Title className="pt-3 ps-2 pb-3 d-flex align-items-center gap-2">Establishments Messages
                    <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <HUBTooltip message="Top 10 establishments (by total error instance count), but limited to the top 5 messages per establishment. This gives a hierarchical view: which establishments have the most errors, and what are their most critical/frequent issues." placement="right">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          size="sm"
                        />
                      </HUBTooltip>
                    </span>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <EstablishmentsMessages data={establishmentsMessagesData} isLoading={isLoadingStatic}/>
                </Card.Body>
              </Card>
            </div>
            <div className="col-6 mt-5">
              <Card className="stats-table-widget h-100">
                <Card.Header>
                  <Card.Title className="pt-3 ps-2 pb-3 d-flex align-items-center gap-2">Establishments by Log Level
                    <span style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <HUBTooltip message="Top 10 establishments with the highest volume of trace, debug, and information level logs. Shows which establishments are generating the most diagnostic/non-error logs." placement="right">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          size="sm"
                        />
                      </HUBTooltip>
                    </span>
                  </Card.Title>
                </Card.Header>
                <Card.Body className="p-0">
                  <LogLevelEstablishments data={logLevelEstablishmentsData} isLoading={isLoadingStatic}/>
                </Card.Body>
              </Card>
            </div>        
          </div>
        </div>
      </Layout>}
    </React.Fragment>
  );
}